import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CalendarCheck, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Switch,
} from "@tabipla/ui";
import { formatDate } from "./utils";

interface CloseScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bestDate?: string;
  isClosing: boolean;
  onConfirm: (applyToTrip: boolean) => Promise<void>;
}

export function CloseScheduleDialog({
  open,
  onOpenChange,
  bestDate,
  isClosing,
  onConfirm,
}: CloseScheduleDialogProps) {
  const { t } = useTranslation();
  const [applyToTrip, setApplyToTrip] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setApplyToTrip(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    await onConfirm(applyToTrip && !!bestDate);
  };

  // Prevent closing during processing
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && isClosing) {
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        onPointerDownOutside={(e) => {
          if (isClosing) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isClosing) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {t("schedules.closeScheduleConfirm.title")}
          </DialogTitle>
          <DialogDescription>{t("schedules.closeScheduleConfirm.description")}</DialogDescription>
        </DialogHeader>

        {bestDate && (
          <div className="space-y-4 py-4">
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                  <CalendarCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    {t("schedules.closeScheduleConfirm.bestDateLabel")}
                  </p>
                  <p className="text-lg font-semibold">{formatDate(bestDate)}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <p className="font-medium">{t("schedules.closeScheduleConfirm.applyToTrip")}</p>
                <p className="text-sm text-muted-foreground">
                  {t("schedules.closeScheduleConfirm.applyToTripDescription")}
                </p>
              </div>
              <Switch checked={applyToTrip} onCheckedChange={setApplyToTrip} />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isClosing}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={isClosing}>
            {isClosing ? t("common.processing") : t("schedules.closeSchedule")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
